import { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import request from "../../../utilities/requests";
import { toast } from "../../../utilities/SnackBarUtils";

export default function TimeForm({ onSubmit, submitting }) {
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [description, setDescription] = useState("");
  const [deliverableType, setDeliverableType] = useState("");
  const [types, setTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(true);

  useEffect(() => {
    const fetchTypes = async () => {
      const result = await request("deliverabletype");
      setTypesLoading(false);
      if (result?.success) setTypes(result.types || []);
    };
    fetchTypes();
  }, []);

  const hoursWorked =
    start && end && dayjs(end).isAfter(dayjs(start))
      ? parseFloat((dayjs(end).diff(dayjs(start), "minute") / 60).toFixed(2))
      : null;

  const handleSubmit = () => {
    if (!deliverableType) {
      toast.error("Please select a work type");
      return;
    }
    if (!start || !end) {
      toast.error("Please set both a start and end time");
      return;
    }
    if (!dayjs(end).isAfter(dayjs(start))) {
      toast.error("End time must be after start time");
      return;
    }
    onSubmit({
      workType: "time",
      deliverableType,
      date: dayjs(start).format("YYYY-MM-DD"),
      start: dayjs(start).format("YYYY-MM-DDTHH:mm:ss"),
      end: dayjs(end).format("YYYY-MM-DDTHH:mm:ss"),
      description,
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
        <FormControl fullWidth disabled={typesLoading}>
          <InputLabel>Work Type</InputLabel>
          <Select
            value={deliverableType}
            label="Work Type"
            onChange={(e) => setDeliverableType(e.target.value)}
          >
            {types.map((t) => (
              <MenuItem key={t._id} value={t._id}>
                {t.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <DateTimePicker
          label="Start Time"
          value={start}
          onChange={(val) => {
            setStart(val);
            if (val) setEnd(dayjs(val).add(1, "hour"));
          }}
          slotProps={{ textField: { fullWidth: true } }}
        />
        <DateTimePicker
          label="End Time"
          value={end}
          onChange={setEnd}
          minDateTime={start || undefined}
          slotProps={{ textField: { fullWidth: true } }}
        />
        <TextField
          label="Hours Worked"
          value={hoursWorked !== null ? `${hoursWorked} hrs` : "—"}
          fullWidth
          InputProps={{ readOnly: true }}
          helperText="Calculated automatically from start and end time"
        />
        <TextField
          label="Description"
          multiline
          rows={4}
          fullWidth
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
          sx={{ py: 1.5 }}
        >
          Submit
        </Button>
      </Box>
    </LocalizationProvider>
  );
}
